import { z } from 'zod';
import { failure, success, type Result } from '../types.js';
import {
  defaultRequiredEmailFamilies,
  isNotificationEvent,
  notificationCatalog,
  type EmailDelivery,
} from './catalog.js';

const channels = z.object({ email: z.boolean(), inApp: z.boolean() }).strict();
const eventSettings = z
  .record(z.string(), channels)
  .refine((values) => Object.keys(values).every(isNotificationEvent));
const policySchema = z
  .object({
    event: z.string().refine(isNotificationEvent),
    organization: eventSettings.optional(),
    personal: eventSettings.optional(),
    requiredEmails: z
      .array(
        z
          .string()
          .refine(
            (key) =>
              isNotificationEvent(key) ||
              defaultRequiredEmailFamilies.some((family) => family === key),
          ),
      )
      .optional(),
    queueEmailMode: z.enum(['per_request', 'digest']).optional(),
    /** The Stock Admin daily digest is on (N3 b, APP-092), whatever the queue email mode. */
    digestEnabled: z.boolean().optional(),
    managerNudgeEnabled: z.boolean().optional(),
    previewing: z.boolean().optional(),
  })
  .strict();

export interface NotificationChannels {
  readonly email: boolean;
  readonly inApp: boolean;
  readonly emailRequired: boolean;
  readonly emailDelivery: EmailDelivery | null;
  /**
   * APP-194: the email would go on its own but for the organization's switch alone (Settings ›
   * Messages' Send, System administration › Notifications' Email), which someone turned off, so
   * it's recorded as turned off in Settings. Never for one off until it's turned on, one the person
   * turned off, one only in the digest, or Preview.
   */
  readonly emailSwitchedOff: boolean;
}
const none: NotificationChannels = {
  email: false,
  inApp: false,
  emailRequired: false,
  emailDelivery: null,
  emailSwitchedOff: false,
};

/** Channel preferences never confer authority. Resolve eligible recipients separately. */
export function notificationChannels(input: unknown): Result<NotificationChannels> {
  const parsed = policySchema.safeParse(input);
  if (!parsed.success) return failure('invalid_notification_policy');
  const policy = parsed.data;
  if (!isNotificationEvent(policy.event)) return failure('unknown_notification_event');
  if (policy.previewing || (policy.event === 'manager_nudge' && !policy.managerNudgeEnabled))
    return success(none);
  const definition = notificationCatalog[policy.event];
  const organization = policy.organization?.[policy.event];
  const personal = policy.personal?.[policy.event];
  const required: readonly string[] = policy.requiredEmails ?? defaultRequiredEmailFamilies;
  const orgEmail = organization?.email ?? definition.defaultEmail;
  const requiredByOrganization =
    required.includes(policy.event) ||
    definition.requiredFamilies.some((family) => required.includes(family));
  // The digest goes out when the organization turns it on, or when the queue's emails go only
  // in it (M5's digest mode needs somewhere to go).
  const cadenceEnabled =
    policy.event !== 'stock_admin_digest' ||
    policy.digestEnabled === true ||
    policy.queueEmailMode === 'digest';
  const emailRequired =
    definition.emailSupported &&
    cadenceEnabled &&
    (definition.alwaysEmail || (orgEmail && requiredByOrganization));
  // The person's own choice, else the notice's starting one (APP-138 §D3), else on.
  const chosenEmail = personal?.email ?? definition.personalDefault?.email ?? true;
  const email =
    definition.emailSupported &&
    cadenceEnabled &&
    (definition.alwaysEmail || (orgEmail && (emailRequired || chosenEmail)));
  // Getting started (K25) and feedback notices are always in the bell, whatever was chosen.
  const inApp =
    definition.bellAlways ||
    ((organization?.inApp ?? definition.defaultInApp) && (personal?.inApp ?? true));
  const emailDelivery =
    policy.event === 'stock_admin_queue' && policy.queueEmailMode === 'digest'
      ? 'digest'
      : definition.emailDelivery;
  // What would go with the organization's switch on: everything else as it is.
  const emailIfOn =
    definition.emailSupported &&
    cadenceEnabled &&
    (definition.alwaysEmail || requiredByOrganization || chosenEmail);
  // Only one someone turned off: an email off until it's turned on (M4) was never sent before.
  const emailSwitchedOff =
    !email && organization?.email === false && emailIfOn && emailDelivery === 'immediate';
  return success({
    email,
    inApp,
    emailRequired,
    emailDelivery: email ? emailDelivery : null,
    emailSwitchedOff,
  });
}

const groupPolicySchema = policySchema
  .pick({ event: true, organization: true, requiredEmails: true, previewing: true })
  .strict();

/** APP-031: one organization-managed Executive group email; no ineffective personal toggle. */
export function notificationGroupChannels(input: unknown): Result<{
  readonly email: boolean;
  readonly emailDelivery: EmailDelivery | null;
  readonly organizationManaged: true;
  /** APP-194: as for one person: the organization's switch alone keeps it from going. */
  readonly emailSwitchedOff: boolean;
}> {
  const parsed = groupPolicySchema.safeParse(input);
  if (
    !parsed.success ||
    !isNotificationEvent(parsed.data.event) ||
    !notificationCatalog[parsed.data.event].audiences.includes('executives')
  )
    return failure('invalid_notification_group_policy');
  const channels = notificationChannels(parsed.data);
  return channels.ok
    ? success({
        email: channels.value.email,
        emailDelivery: channels.value.emailDelivery,
        organizationManaged: true,
        emailSwitchedOff: channels.value.emailSwitchedOff,
      })
    : channels;
}
